import { useQuery } from '@tanstack/react-query'
import { useIntl } from 'react-intl'

import type { TLocation } from '../../settings/api/types/editAccountSettings.types'
import { geoApi } from '../api/geoApi'
import type { TAddressComponent, TGetPlaceDetailsData } from '../api/types.ts/getPlaceDetails.types'

const transformData = (data: TGetPlaceDetailsData) => {
  const componentsMap = {
    postal_code: 'zipcode',
    locality: 'city',
    country: 'countryCode',
  } as const

  // Default values
  const location: TLocation = {
    zipcode: null,
    city: '',
    countryCode: '',
    formattedAddress: data.formattedAddress || '',
    latitude: data.location.latitude || null,
    longitude: data.location.longitude || null,
  }

  data.addressComponents.forEach((infos: TAddressComponent) => {
    const type = infos.types.find((t): t is keyof typeof componentsMap => t in componentsMap)
    if (type) {
      const mappedType = componentsMap[type]
      if (mappedType === 'countryCode') {
        location[mappedType] = infos.shortText
      } else {
        location[mappedType] = infos.longText
      }
    }
  })

  return location
}

export const usePlaceDetailsQuery = (placeId?: string) => {
  const { locale } = useIntl()

  return useQuery({
    enabled: !!placeId,
    queryKey: ['place_details', placeId, locale],
    // oxlint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by enabled
    queryFn: ({ signal }) => geoApi.getPlaceDetails({ placeId: placeId!, locale }, { signal }),
    select: (data) => transformData(data),
  })
}
